import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, Monitor, Award, User, Mail, Phone, GraduationCap } from 'lucide-react';
import { courses } from '../data/mockData';

const EnrollPage = () => {
  const { courseId } = useParams();
  const course = courses.find((c) => c.id === courseId);
  const [pageVisible, setPageVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    qualification: '',
    experience: '',
    message: ''
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = course
      ? `Enroll — ${course.title} | Insust Global`
      : 'Enroll | Insust Global';
    const t = setTimeout(() => setPageVisible(true), 50);
    return () => clearTimeout(t);
  }, [courseId, course]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  if (!course) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Course Not Found</h1>
          <Link to="/" className="text-teal-400 hover:text-teal-300">&larr; Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-slate-950 transition-opacity duration-700 ${pageVisible ? 'opacity-100' : 'opacity-0'}`}>

      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 lg:px-12 h-16 bg-slate-950/90 backdrop-blur-lg border-b border-slate-800/50">
        <Link to="/" className="flex items-center gap-3">
          <img src="/logo.jpeg" alt="Insust Global logo" className="h-8 w-8 rounded-full object-contain" />
          <span className="text-lg font-bold text-white">Insust Global</span>
        </Link>
        <Link to="/#courses" className="flex items-center gap-2 text-sm text-slate-400 hover:text-teal-400 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Courses
        </Link>
      </nav>

      <div className="pt-16 min-h-screen flex items-center justify-center px-6 py-16">
        <div className="max-w-[1100px] w-full mx-auto">
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">

            {/* Left — Course Summary */}
            <div className="lg:sticky lg:top-24">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-teal-500/20 bg-teal-500/5 mb-6 text-xs font-mono tracking-wider uppercase text-teal-400">
                Course {course.number}
              </span>

              <h1 className="text-3xl lg:text-4xl font-bold text-white mb-3 leading-tight">
                {course.title}
              </h1>
              <p className="text-sm text-slate-400 leading-relaxed mb-6">
                {course.description}
              </p>

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 text-sm text-slate-300">
                  <Clock className="w-4 h-4 text-teal-400 flex-shrink-0" />
                  <span>{course.duration} &middot; {course.format}</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-slate-300">
                  <Monitor className="w-4 h-4 text-teal-400 flex-shrink-0" />
                  <span>{course.cohort} &middot; {course.cohortLabel}</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-slate-300">
                  <Award className="w-4 h-4 text-teal-400 flex-shrink-0" />
                  <span>Certificate of Completion</span>
                </div>
              </div>

              <div className="p-5 rounded-xl bg-slate-900/60 border border-slate-800">
                <div className="text-[10px] font-mono tracking-wider uppercase text-slate-500 mb-1">Tuition</div>
                <div className="flex items-baseline gap-3">
                  <span className="text-3xl font-bold text-white">{course.price}</span>
                  {course.proPrice && (
                    <span className="text-sm font-mono text-teal-400">
                      Pro {course.proPrice}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Use code <strong className="text-teal-400">SPATIAL15</strong> for 15% early enrollment discount
                </p>
              </div>
            </div>

            {/* Right — Enrollment Form */}
            <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-8 lg:p-10">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-teal-500/10 border border-teal-500/30 flex items-center justify-center mx-auto mb-6">
                    <Award className="w-8 h-8 text-teal-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-3">Enrollment Submitted</h2>
                  <p className="text-slate-400 mb-6 max-w-sm mx-auto">
                    Thank you, {form.name}! We've received your enrollment request for <strong className="text-white">{course.title}</strong>. Our team will reach out to you at <strong className="text-teal-400">{form.email}</strong> within 24 hours.
                  </p>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold text-sm rounded-lg transition-all"
                  >
                    Back to Home
                  </Link>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-white mb-1">Enroll Now</h2>
                  <p className="text-sm text-slate-400 mb-8">Fill in your details to secure your seat</p>

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <label htmlFor="name" className="block text-xs font-mono tracking-wider uppercase text-slate-500 mb-2">
                        Full Name *
                      </label>
                      <div className="relative">
                        <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={form.name}
                          onChange={handleChange}
                          placeholder="Your full name"
                          className="w-full pl-11 pr-4 py-3 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/30 transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-xs font-mono tracking-wider uppercase text-slate-500 mb-2">
                        Email Address *
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={form.email}
                          onChange={handleChange}
                          placeholder="you@example.com"
                          className="w-full pl-11 pr-4 py-3 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/30 transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-xs font-mono tracking-wider uppercase text-slate-500 mb-2">
                        Phone Number *
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <input
                          id="phone"
                          name="phone"
                          type="tel"
                          required
                          value={form.phone}
                          onChange={handleChange}
                          placeholder="+91 98765 43210"
                          className="w-full pl-11 pr-4 py-3 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/30 transition-colors"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="qualification" className="block text-xs font-mono tracking-wider uppercase text-slate-500 mb-2">
                          Qualification
                        </label>
                        <div className="relative">
                          <GraduationCap className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                          <select
                            id="qualification"
                            name="qualification"
                            value={form.qualification}
                            onChange={handleChange}
                            className="w-full pl-11 pr-4 py-3 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/30 transition-colors appearance-none"
                          >
                            <option value="" className="text-slate-600">Select</option>
                            <option value="student">Student</option>
                            <option value="bachelors">Bachelor's Degree</option>
                            <option value="masters">Master's Degree</option>
                            <option value="phd">PhD / Doctorate</option>
                            <option value="professional">Working Professional</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label htmlFor="experience" className="block text-xs font-mono tracking-wider uppercase text-slate-500 mb-2">
                          GIS Experience
                        </label>
                        <select
                          id="experience"
                          name="experience"
                          value={form.experience}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/30 transition-colors appearance-none"
                        >
                          <option value="" className="text-slate-600">Select</option>
                          <option value="none">No experience</option>
                          <option value="beginner">Beginner (0-1 years)</option>
                          <option value="intermediate">Intermediate (1-3 years)</option>
                          <option value="advanced">Advanced (3+ years)</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-xs font-mono tracking-wider uppercase text-slate-500 mb-2">
                        Anything else? (optional)
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={3}
                        value={form.message}
                        onChange={handleChange}
                        placeholder="Goals, questions, or how you heard about us..."
                        className="w-full px-4 py-3 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/30 transition-colors resize-none"
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full py-4 bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold text-sm uppercase tracking-wider rounded-lg transition-all hover:shadow-lg hover:shadow-teal-500/20 active:translate-y-0.5"
                    >
                      Submit Enrollment
                    </button>

                    <p className="text-[11px] text-slate-600 text-center">
                      By enrolling, you agree to our terms and privacy policy. Our team will confirm your seat via email.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-950 border-t border-slate-800 py-6 px-6 lg:px-16 flex flex-col sm:flex-row items-center justify-between gap-4">
        <span className="text-xs text-slate-600">&copy; {new Date().getFullYear()} Insust Global. All rights reserved.</span>
        <div className="flex gap-4 text-xs text-slate-600">
          <span className="hover:text-teal-400 cursor-pointer transition-colors">Privacy Policy</span>
          <span className="hover:text-teal-400 cursor-pointer transition-colors">Terms of Service</span>
          <Link to="/" className="hover:text-teal-400 transition-colors">Home</Link>
        </div>
      </footer>
    </div>
  );
};

export default EnrollPage;
